/**
 * useShipmentTimeline - Andamentos (comentários + anexos) e eventos da remessa.
 *
 * Mesmo contrato de dados da timeline da O.S. (`ServiceOrderTimelineItem`) para
 * reaproveitar a aba Andamentos sem duplicar a interface.
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial (RPCs *_shipment_comment, get_shipment_timeline).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { validateFile } from '@/hooks/useTaskAttachments';
import { sanitizeStorageFileName } from '@/lib/storageFileName';
import { createLogger } from '@/lib/logger';
import type { ServiceOrderTimelineItem } from '@/hooks/useServiceOrderDetails';

const logger = createLogger('ShipmentTimeline');
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);
const BUCKET = 'shipments';
export const SHIPMENT_COMMENT_MAX = 5000;

/** Classifica o arquivo (mesma regra dos anexos da O.S.). */
function fileType(file: File): string {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  return 'document';
}

/** Envia o anexo ao bucket privado e registra; remove o arquivo se o registro falhar. */
export async function uploadShipmentAttachment(p: { orderId: string; commentId: string; tenantId: string; file: File }): Promise<void> {
  const v = validateFile(p.file);
  if (!v.valid) throw new Error(v.error);
  const path = `${p.tenantId}/shipments/${p.orderId}/${crypto.randomUUID()}_${sanitizeStorageFileName(p.file.name)}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, p.file, {
    cacheControl: '3600', upsert: false, contentType: p.file.type || 'application/octet-stream',
  });
  if (upErr) throw new Error('Falha ao enviar o arquivo');
  const { error } = await rpc('register_shipment_attachment', {
    p_comment_id: p.commentId, p_file_name: p.file.name, p_file_type: fileType(p.file),
    p_mime_type: p.file.type || 'application/octet-stream', p_file_size: p.file.size,
    p_storage_path: path, p_tenant_hint: p.tenantId,
  });
  if (!error) return;
  await supabase.storage.from(BUCKET).remove([path]);
  logger.error('Erro ao registrar anexo da remessa', { error: error.message });
  throw error;
}

/** URL temporária (10 min) do anexo privado. */
export async function getShipmentAttachmentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 600);
  if (error || !data?.signedUrl) throw new Error('Não foi possível preparar o arquivo');
  return data.signedUrl;
}

/** Remove arquivos do bucket após exclusão no banco (falha não bloqueia a tela). */
async function removePaths(paths?: string[]) {
  if (!paths?.length) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) logger.warn('Falha ao remover anexos da remessa', { error: error.message });
}

export function useShipmentTimeline(shipmentId: string | null) {
  const { currentTenant } = useAuth();
  const hint = currentTenant?.id ?? null;
  const qc = useQueryClient();
  const key = ['shipment-timeline', hint, shipmentId];
  const refresh = () => qc.invalidateQueries({ queryKey: ['shipment-timeline'] });

  const timeline = useQuery({
    queryKey: key,
    enabled: !!hint && !!shipmentId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('get_shipment_timeline', { p_shipment_id: shipmentId, p_tenant_hint: hint });
      if (error) throw error;
      return (data ?? []) as ServiceOrderTimelineItem[];
    },
  });

  const addComment = useMutation({
    mutationFn: async ({ content, contentHtml, mentionedUserIds }: { content: string; contentHtml?: string; mentionedUserIds?: string[] }) => {
      const { data, error } = await rpc('add_shipment_comment', {
        p_shipment_id: shipmentId, p_content: content.slice(0, SHIPMENT_COMMENT_MAX),
        p_content_html: contentHtml?.slice(0, 20000) || null,
        p_mentioned_user_ids: mentionedUserIds?.slice(0, 20) ?? [], p_tenant_hint: hint,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: refresh,
  });

  const updateComment = useMutation({
    mutationFn: async (i: { commentId: string; content: string; contentHtml?: string; mentionedUserIds?: string[]; removedAttachmentIds?: string[] }) => {
      const { data, error } = await rpc('update_shipment_comment', {
        p_comment_id: i.commentId, p_content: i.content.slice(0, SHIPMENT_COMMENT_MAX),
        p_content_html: i.contentHtml?.slice(0, 20000) || null,
        p_mentioned_user_ids: i.mentionedUserIds?.slice(0, 20) ?? [],
        p_removed_attachment_ids: i.removedAttachmentIds?.slice(0, 20) ?? [], p_tenant_hint: hint,
      });
      if (error) throw error;
      const r = data as { removed_paths?: string[] };
      await removePaths(r?.removed_paths);
      return r;
    },
    onSuccess: refresh,
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { data, error } = await rpc('delete_shipment_comment', { p_comment_id: commentId, p_tenant_hint: hint });
      if (error) throw error;
      await removePaths((data as { removed_paths?: string[] })?.removed_paths);
    },
    onSuccess: refresh,
  });

  return { timeline, addComment, updateComment, deleteComment };
}
